'use client';

import { useState, useId } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import {
  CheckCircle, Send, User, Building2, Mail, Briefcase, MessageSquare, ChevronDown, Phone,
} from 'lucide-react';
import PhoneInput from './PhoneInput';
import { ConsentSection } from '@/components/forms/ConsentSection';

const spinKeyframes = `
@keyframes cmd-spin {
  to { transform: rotate(360deg); }
}
`;

  const floatCSS = `
.floating-field input,
.floating-field select,
.floating-field textarea {
  padding-top: 1rem;
  padding-bottom: 0.5rem;
}
.floating-field label {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  transform-origin: left center;
  pointer-events: none;
  transition: top 0.18s ease, transform 0.18s ease, font-size 0.18s ease, color 0.18s ease, background-color 0.18s ease;
  font-size: 0.8rem;
  font-weight: 500;
  color: #64748b;
  background: transparent;
  padding: 0 0.25rem;
  text-wrap: nowrap;
}
.floating-field textarea ~ label {
  top: 0.875rem;
  transform: none;
}
.floating-field input:focus ~ label,
.floating-field select:focus ~ label,
.floating-field textarea:focus ~ label,
.floating-field input:not(:placeholder-shown) ~ label,
.floating-field select:not([value=""]) ~ label,
.floating-field textarea:not(:placeholder-shown) ~ label {
  top: 0;
  transform: translateY(-50%);
  font-size: 0.7rem;
  font-weight: 600;
  color: #1e5f9d;
  background: #f8faff;
  padding: 0 0.35rem;
  letter-spacing: 0.01em;
}
.floating-field textarea:focus ~ label,
.floating-field textarea:not(:placeholder-shown) ~ label {
  top: 0;
  transform: translateY(-50%);
}
.floating-field select {
  background-image: none;
}
`;

function FloatingInput({ id, label, icon, type = 'text', required, value, onChange, onBlur, error, phoneOnly }: {
  id: string; label: string; icon: React.ReactNode; type?: string; required?: boolean; value: string; onChange: (e: { target: { value: string } }) => void; onBlur?: () => void; error?: string; phoneOnly?: boolean;
}) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (phoneOnly) {
      const allowed = /^[+\d\s\-()]*$/;
      if (!allowed.test(val)) {
        e.target.value = val.replace(/[^+\d\s\-()]/g, '');
      }
    }
    onChange(e);
  };
  const hasError = !!error && !!value;
  return (
    <div className="space-y-1.5">
      <div className="floating-field relative">
        <input id={id} name={id} type={type} required={required} value={value} onChange={handleChange} onBlur={onBlur} placeholder=" "
          className={`block w-full rounded-xl border bg-transparent px-3 pb-2 pt-4 text-[13px] text-slate-900 transition-colors duration-200 focus:ring-0 focus:shadow-[0_0_0_3px_rgba(44,120,185,0.08)] ${hasError ? 'border-red-400 focus:border-red-500' : 'border-slate-200 focus:border-[#2C78B9]'}`} />
        <label htmlFor={id} className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 flex items-center justify-center">{icon}</span>{label}
        </label>
      </div>
      {hasError && <p className="text-xs text-red-600 font-medium">{error}</p>}
    </div>
  );
}

function FloatingSelect({ id, label, icon, value, onChange, options }: {
  id: string; label: string; icon: React.ReactNode; value: string; onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void; options: { value: string; label: string; disabled?: boolean }[];
}) {
  return (
    <div className="floating-field relative">
      <select id={id} name={id} value={value} onChange={onChange}
        className="block w-full appearance-none rounded-xl border border-slate-200 bg-transparent px-3 pb-2 pt-4 text-[13px] text-slate-900 transition-colors duration-200 focus:border-[#2C78B9] focus:ring-0 focus:shadow-[0_0_0_3px_rgba(44,120,185,0.08)]"
      >
        {options.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
      </select>
      <label htmlFor={id} className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 flex items-center justify-center">{icon}</span>{label}
      </label>
      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
        <ChevronDown size={14} className="text-slate-400" />
      </span>
    </div>
  );
}

function FloatingTextarea({ id, label, icon, value, onChange, rows = 3 }: {
  id: string; label: string; icon: React.ReactNode; value: string; onChange: (e: { target: { value: string } }) => void; rows?: number;
}) {
  return (
    <div className="floating-field relative">
      <textarea id={id} name={id} rows={rows} value={value} onChange={onChange} placeholder=" "
        className="block w-full resize-none rounded-xl border border-slate-200 bg-transparent px-3 pb-2 pt-4 text-[13px] text-slate-900 transition-colors duration-200 focus:border-[#2C78B9] focus:ring-0 focus:shadow-[0_0_0_3px_rgba(44,120,185,0.08)]" />
      <label htmlFor={id} className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 flex items-center justify-center">{icon}</span>{label}
      </label>
    </div>
  );
}

import { validatePhone } from "@/utils/phoneValidation";
import { phoneCountryToIso } from "@/data/countryCodes";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface LeadCapturePopupProps {
  triggerLabel?: string;
  headline?: string;
  subheadline?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  successRedirect?: string;
}

const industryOptions = [
  { value: '__placeholder', label: 'Select your industry', disabled: true },
  { value: 'mx-erp-mfg', label: 'Mx-ERP — Manufacturing & Trading' },
  { value: 'mx-erp-con', label: 'Mx-ERP — Construction & EPC' },
  { value: 'electra-his', label: 'Electra-HIS — Healthcare Solutions' },
  { value: 'mx-crm', label: 'Mx-CRM — Lead Management' },
  { value: 'general', label: 'General Enquiry' },
  { value: 'partnership', label: 'Partnership / Collaboration' },
  { value: 'support', label: 'Support / Existing Customer' },
];

export default function LeadCapturePopup({
  triggerLabel = 'Request Free Demo',
  headline = 'Schedule Your Free ERP Consultation',
  subheadline = "Explore how ACGIL's 30+ years of ERP expertise can streamline your operations. Our experts will walk you through a tailored solution.",
  open,
  onOpenChange,
  successRedirect,
}: LeadCapturePopupProps) {
  const id = useId();
  const router = useRouter();
  const reducedMotion = useReducedMotion();

  const isControlled = open !== undefined && onOpenChange !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const dialogOpen = isControlled ? open : internalOpen;
  const setDialogOpen = isControlled ? onOpenChange! : setInternalOpen;

  const [formData, setFormData] = useState({
    full_name: '', company: '', email: '', phone: '', industry: '', message: ''
  });
  const [phoneCountry, setPhoneCountry] = useState('+91');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({ phone: undefined });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const update = (k: string) => (
    e: { target: { value: string } }
  ) => {
    setFormData({ ...formData, [k]: e.target.value });
    if (k === 'phone') {
      setErrors((prev) => ({ ...prev, phone: undefined }));
    }
  };

  const handleBlur = async () => {
    const countryIso = phoneCountryToIso[phoneCountry] || 'US';
    const result = await validatePhone(formData.phone, countryIso);
    setErrors({ phone: result.error });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const countryIso = phoneCountryToIso[phoneCountry] || 'US';
    const result = await validatePhone(formData.phone, countryIso);
    const phoneErr = result.error;
    const nextErrors = { phone: phoneErr };
    setErrors(nextErrors);
    if (phoneErr) return;

    setStatus('loading');
    try {
      const requestType = formData.industry || 'general';
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.full_name,
          company: formData.company || null,
          email: formData.email,
          phone: phoneCountry ? `${phoneCountry} ${formData.phone}`.trim() : formData.phone,
          message: formData.message || null,
          requestType,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to send message');
      }

      setStatus('success');
      if (successRedirect) router.push(successRedirect);
      setTimeout(() => {
        setStatus('idle');
        setFormData({ full_name: '', company: '', email: '', phone: '', industry: '', message: '' });
        setErrors({});
        if (isControlled && onOpenChange) {
          onOpenChange(false);
        }
      }, 2500);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  const duration = reducedMotion ? 0 : 0.4;
  const leftDelay = reducedMotion ? 0 : 0.08;

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent
        showCloseButton={false}
        className="!max-w-4xl !p-0 !rounded-none !shadow-2xl !shadow-primary/10 !border !border-slate-200/80 !backdrop-blur-sm animate-popup-enter"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Request a Demo</DialogTitle>
          <DialogDescription>
            Fill in your details and our team will reach out within 24 hours.
          </DialogDescription>
        </DialogHeader>

        <button
          type="button"
          onClick={() => setDialogOpen(false)}
          className="absolute top-2 right-2 sm:top-3 sm:right-3 z-50 flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-white text-slate-600 shadow-lg ring-1 ring-slate-200 transition-all duration-200 hover:bg-slate-50 hover:text-slate-800 hover:shadow-xl hover:scale-105"
          aria-label="Close dialog"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-gradient-to-br from-primary via-primary to-primary-dark p-6 lg:p-8">
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute rounded-full bg-white/5" style={{ width: 340, height: 340, filter: 'blur(60px)', top: -100, right: -100 }} />
              <div className="absolute rounded-full bg-accent/10" style={{ width: 260, height: 260, filter: 'blur(50px)', bottom: -60, left: -60 }} />
              <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
            </div>

            <div className="relative z-10 flex h-full flex-col justify-between space-y-4">
              <div className="space-y-2">
                <h2 className="text-xl lg:text-[1.45rem] font-extrabold text-white leading-tight tracking-tight">{headline}</h2>
                <p className="text-xs text-white/80 leading-relaxed max-w-sm">{subheadline}</p>
              </div>

              <div className="space-y-2">
                {[
                  '30+ years of ERP expertise',
                  'Tailored industry solutions',
                  'Dedicated implementation support',
                ].map((text, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-white/90 text-[11px]">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white/15">
                      <CheckCircle size={12} />
                    </span>
                    <span className="leading-relaxed">{text}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-[10px] font-semibold text-white/80">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <Mail size={11} />
                  </span>
                  <span>akg@acgil.net</span>
                </div>
                <div className="flex items-center gap-2 text-[10px] font-semibold text-white/80">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <Phone size={11} />
                  </span>
                  <span>+91 98 18 993537</span>
                </div>
              </div>
            </div>
          </div>

          <style>{spinKeyframes}</style>
          <style>{floatCSS}</style>
          <div className="relative bg-white p-6 lg:p-8">
            <div className="pointer-events-none absolute top-0 right-0 h-20 w-20 bg-[#2176B3] sm:h-24 sm:w-24 lg:h-28 lg:w-28" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)', opacity: 0.12 }} />
            <div className="relative z-10 mx-auto w-full max-w-xl">
              <motion.div custom={0.3} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: duration, delay: 0.3 }}>
                <h3 className="text-lg font-bold text-slate-900">Send us a message</h3>
                <p className="mt-1 text-[13px] text-[#64748b]">Fill in the details below and our team will be in touch.</p>
              </motion.div>

              <form onSubmit={handleSubmit} className="mt-5 space-y-3">
                <motion.div custom={0.4} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: duration, delay: 0.4 }} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FloatingInput id={`${id}-full_name`} label="Full Name" icon={<User size={13} />} required value={formData.full_name} onChange={update('full_name')} />
                  <FloatingInput id={`${id}-email`} label="Email Address" icon={<Mail size={13} />} type="email" required value={formData.email} onChange={update('email')} />
                </motion.div>

                <motion.div custom={0.45} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: duration, delay: 0.45 }}>
                  <PhoneInput id={`${id}-phone`} label="Phone Number" value={formData.phone} onChange={update('phone')} onCountryChange={(code) => setPhoneCountry(code.prefix)} onBlur={handleBlur} error={errors.phone} required />
                </motion.div>

                <motion.div custom={0.5} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: duration, delay: 0.5 }}>
                  <FloatingInput id={`${id}-company`} label="Company Name" icon={<Building2 size={13} />} value={formData.company} onChange={update('company')} />
                </motion.div>

                <motion.div custom={0.5} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: duration, delay: 0.5 }}>
                  <FloatingSelect id={`${id}-industry`} label="I'm interested in..." icon={<Briefcase size={13} />} value={formData.industry || '__placeholder'} onChange={update('industry')} options={industryOptions} />
                </motion.div>

                <motion.div custom={0.55} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: duration, delay: 0.55 }}>
                  <FloatingTextarea id={`${id}-message`} label="Your Message" icon={<MessageSquare size={13} />} value={formData.message} onChange={update('message')} rows={3} />
                </motion.div>

                <motion.div custom={0.6} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: duration, delay: 0.6 }}>
                  <ConsentSection required />
                </motion.div>

                <motion.div custom={0.65} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                   transition={{ duration, delay: 0.65 }}>
                  <button type="submit" disabled={status === 'loading' || status === 'success' || status === 'error'}
                    className="mt-1 flex h-[52px] w-full items-center justify-center gap-2 rounded-xl font-bold text-white transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-80"
                    style={{
                      background: status === 'success' ? 'linear-gradient(135deg, #059A8F, #047a71)' : 'linear-gradient(135deg, #2C78B9, #1a5fa0)',
                      boxShadow: status === 'success' ? '0 6px 18px rgba(5,154,143,0.35)' : '0 6px 18px rgba(44,120,185,0.3)',
                    }}
                    onMouseEnter={(e) => { if (status !== 'success') { (e.currentTarget as HTMLElement).style.background = 'linear-gradient(135deg, #1a5fa0, #2C78B9)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 10px 26px rgba(44,120,185,0.45)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; } }}
                    onMouseLeave={(e) => { if (status !== 'success') { (e.currentTarget as HTMLElement).style.background = 'linear-gradient(135deg, #2C78B9, #1a5fa0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 18px rgba(44,120,185,0.3)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; } }}
                    onMouseDown={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                    onMouseUp={(e) => { if (status !== 'success') (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
                  >
                    {status === 'loading' && (
                      <span className="inline-block h-3.5 w-3.5 rounded-full" style={{ border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', animation: 'cmd-spin 0.6s linear infinite' }} />
                    )}
                    {status === 'success' && <CheckCircle size={14} />}
                    {status === 'idle' && <Send size={14} />}
                    <span className="text-[13px]">
                      {status === 'loading' ? 'Sending...' : status === 'success' ? 'Message Sent!' : 'Send Message'}
                    </span>
                  </button>
                </motion.div>
              </form>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
